import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Star, MapPin, Users, Clock, Headphones } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import heroBg from "@/assets/hero-bg.jpg";

const NewHero = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (sectionRef.current) {
            const scrollY = window.scrollY;
            sectionRef.current.style.setProperty('--scroll-y', `${scrollY * 0.3}px`);
            sectionRef.current.style.setProperty('--scroll-y-slow', `${scrollY * 0.15}px`);
            sectionRef.current.style.setProperty('--scroll-y-slower', `${scrollY * 0.1}px`);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-[90vh] flex items-center pt-20 overflow-hidden" style={{ '--scroll-y': '0px', '--scroll-y-slow': '0px', '--scroll-y-slower': '0px' } as React.CSSProperties}>
      {/* Background with parallax */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroBg} 
          alt="Centro de operaciones de seguridad" 
          className="w-full h-full object-cover"
          fetchPriority="high"
          style={{ 
            transform: 'translateY(var(--scroll-y)) scale(1.1)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-tera-dark via-tera-dark/95 to-tera-dark/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-tera-dark via-transparent to-transparent" />
      </div>

      {/* Decorative elements with parallax */}
      <div 
        className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px] animate-pulse"
        style={{ transform: 'translateY(var(--scroll-y-slow))' }}
      />
      <div 
        className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-secondary/10 rounded-full blur-[80px]"
        style={{ transform: 'translateY(var(--scroll-y-slower))' }}
      />

      <div className="container px-4 sm:px-6 relative z-10">
        <div className="max-w-3xl">
          {/* Trust badge */}
          <div className="inline-flex items-center gap-2 sm:gap-3 bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 rounded-full px-3 sm:px-4 py-2 mb-6 sm:mb-8 animate-fade-in">
            <div className="flex items-center gap-0.5 sm:gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-secondary text-secondary" />
              ))}
            </div>
            <span className="text-primary-foreground/90 text-xs sm:text-sm font-medium">
              4.9/5 en Google · +70 valoraciones
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-4 sm:mb-6 leading-[1.1] animate-fade-up">
            Reduce pérdidas y mejora el control con sistemas{" "}
            <span className="text-gradient-gold">que sí funcionan.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-lg md:text-xl text-primary-foreground/80 mb-8 sm:mb-10 leading-relaxed max-w-2xl animate-fade-up" style={{ animationDelay: "0.1s" }}>
            Instalación y mantenimiento de videovigilancia, control de acceso y asistencia, redes, alarmas y telefonía para empresas. Diseño de ingeniería, optimización de recursos y cero sorpresas.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <Button variant="hero" size="xl" className="group" asChild>
              <Link to="/contacto">
                Evaluación sin costo esta semana
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button variant="heroOutline" size="xl" className="group" asChild>
              <a href="#proceso">
                Ver cómo trabajamos
                <Calendar className="w-5 h-5" />
              </a>
            </Button>
          </div>

          {/* Social proof indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            {[
              { icon: Clock, label: "Desde 2011" },
              { icon: Users, label: "Equipo técnico" },
              { icon: MapPin, label: "Dirección física" },
              { icon: Headphones, label: "Soporte post-instalación" },
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 text-primary-foreground/70"
              >
                <item.icon className="w-4 h-4 text-primary" />
                <span className="text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewHero;
